import { styled } from 'goober';
import type { ChangeEvent } from 'react';
import { useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { SEARCH_INPUT_MAX_CHAR } from '@/constants';
import { useFocusOnMount } from '@/hooks/use-focus-on-mount';
import { colors } from '@/theme/colors';
import { Input } from '@/ui/input/input';
import { Stack } from '@/ui/line/line';

import { useDebouncedCallback } from 'use-debounce';
import { SearchContent } from './search-content';
import { defaultSearchQueryState, searchQueryState } from './search.state';
import { useSetAtom } from 'jotai';

export const Search = () => {
  const { t } = useTranslation('default', { keyPrefix: 'globalSearch' });

  const setSearchQuery = useSetAtom(searchQueryState);
  const resetSearchQuery = () => setSearchQuery(defaultSearchQueryState);
  const searchRef = useRef<HTMLInputElement>(null);
  useFocusOnMount(searchRef);

  // Create a debounced version of setSearchQuery
  const debouncedSetSearchQuery = useDebouncedCallback(
    (value: string) => {
      setSearchQuery(prevQuery => ({
        ...prevQuery,
        search: value,
        page: 1,
      }));
    },
    300, // Reduced from 500ms to 300ms for better responsiveness
    {
      maxWait: 800, // Reduced from 1000ms to 800ms
      leading: false, // Removed leading for consistent behavior
    },
  );

  useEffect(() => {
    return () => {
      // Give a brief grace period before cancelling to allow quick navigation searches
      setTimeout(() => {
        debouncedSetSearchQuery.cancel();
      }, 50); // 50ms grace period

      resetSearchQuery();
    };
  }, [debouncedSetSearchQuery, resetSearchQuery]);

  // Immediate handler for the input field
  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value ?? '';
      debouncedSetSearchQuery(value);
    },
    [debouncedSetSearchQuery],
  );

  return (
    <SearchContainer gap="20px">
      <Input
        name="search"
        ref={searchRef}
        leftIcon="Search"
        iconSize="26px"
        iconColor={colors.gray.c7}
        padding="10px 8px"
        fontSize="16px"
        gap="12px"
        placeholder={t`searchPlaceholder`}
        onChange={handleChange}
        maxLength={SEARCH_INPUT_MAX_CHAR}
      />
      <SearchContent />
    </SearchContainer>
  );
};

const SearchContainer = styled(Stack)`
  flex: 1;
`;
